import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import useSeo from "@/hooks/useSeo";
import { siteInfo } from "@/mocks/site";
import ContactForm from "@/pages/contact/components/ContactForm";

const channelList = [
  {
    icon: "ri-mail-line",
    title: "邮件联系",
    value: siteInfo.email,
    desc: "适合信息更正、转载授权等需要附材料的沟通。",
  },
  {
    icon: "ri-time-line",
    title: "响应时间",
    value: siteInfo.workingHours,
    desc: "周末与节假日同样处理，紧急事项请在标题注明。",
  },
  {
    icon: "ri-wechat-line",
    title: "微信通道",
    value: siteInfo.wechat,
    desc: "添加时请备注来意，便于我们优先处理线索类信息。",
  },
];

export default function Contact() {
  useSeo({
    title: "联系我们 - 17爆料网线索投稿与信息更正",
    description:
      "通过联系页面向17爆料网提交公开线索、信息更正或转载授权申请。请提供公开可查的来源链接，我们会在两个工作日内回复。",
    keywords: "联系我们,线索投稿,信息更正,转载授权,17爆料网",
    path: "/contact",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系我们 - 17爆料网",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://17baoliao.example.com/" },
          { "@type": "ListItem", position: 2, name: "联系我们", item: "https://17baoliao.example.com/contact" },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full flex-1">
        <div className="w-full px-4 pt-6 md:px-8">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
        </div>

        <section className="w-full px-4 pb-8 pt-6 md:px-8">
          <h1 className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-4xl">联系我们</h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            无论是提供公开线索、发来更正材料，还是希望转载内容，都可以通过下面的表单与我们联系。请尽量附上可核对的来源，这会大幅加快处理速度。
          </p>
        </section>

        <section className="w-full px-4 pb-14 md:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_340px] lg:items-start">
            <ContactForm />

            <aside className="flex flex-col gap-5">
              {channelList.map((channel) => (
                <div key={channel.title} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className={`${channel.icon} text-xl`}></i>
                  </span>
                  <div className="min-w-0">
                    <h2 className="font-heading text-base font-bold text-foreground-950">{channel.title}</h2>
                    <p className="mt-1 break-all text-sm font-medium text-primary-700">{channel.value}</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{channel.desc}</p>
                  </div>
                </div>
              ))}

              <div className="rounded-lg border border-accent-300 bg-accent-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-accent-950">
                  <i className="ri-information-line text-lg text-accent-700"></i>
                  提交前请留意
                </h2>
                <ul className="mt-3 space-y-2 text-sm leading-relaxed text-accent-900">
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-600"></i>
                    只提供公开可见的信息与链接
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-600"></i>
                    不提交涉及个人隐私的内容
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[6px] text-accent-600"></i>
                    更正请求请附上可核对的材料
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">常见问题</h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  提交前可以先看看常见问题，大多疑问已经在那里说明。
                </p>
                <Link
                  to="/faq"
                  className="mt-3 inline-flex items-center gap-1 text-sm text-primary-600 transition-colors hover:text-primary-700 cursor-pointer"
                >
                  查看常见问题
                  <i className="ri-arrow-right-line text-base"></i>
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}